/**
 * Single source for browser auth session storage key and Bearer extraction.
 * Optional override: VITE_AUTH_SESSION_STORAGE_KEY (build-time).
 */

const DEFAULT_SESSION_KEY = "sample-ui:auth-session:v1";

export function getAuthSessionStorageKey() {
  const k = import.meta.env?.VITE_AUTH_SESSION_STORAGE_KEY;
  if (k != null && String(k).trim() !== "") return String(k).trim();
  return DEFAULT_SESSION_KEY;
}

/**
 * Keycloak-derived grants required before protected routes may load (SPA gate).
 * @param {Record<string, unknown> | null | undefined} parsed
 */
export function hasValidLoginGrants(parsed) {
  if (!parsed?.token || typeof parsed.token !== "string") return false;
  const tid = String(parsed.tenant_id ?? "").trim();
  const roles = Array.isArray(parsed.roles) ? parsed.roles.filter(Boolean) : [];
  if (!tid || roles.length === 0) return false;
  return true;
}

export function readStoredSession() {
  if (typeof window === "undefined") return null;
  try {
    const key = getAuthSessionStorageKey();
    const raw =
      window.localStorage.getItem(key) || window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Bearer token for Socket.IO / API — session stores `token`, not `accessToken`. */
export function getStoredAccessToken() {
  const session = readStoredSession();
  if (!session) return null;
  const t = session.token ?? session.accessToken ?? session.access_token;
  return t && String(t).trim() ? String(t).trim() : null;
}

export function getBearerAuthHeaders() {
  const parsed = readStoredSession();
  if (!parsed?.token) return {};
  return { Authorization: `Bearer ${parsed.token}` };
}

export function isStoredSessionPersistent() {
  if (typeof window === "undefined") return false;
  try {
    const key = getAuthSessionStorageKey();
    return window.localStorage.getItem(key) != null;
  } catch {
    return false;
  }
}

export function writeStoredSession({
  token,
  email,
  rememberMe,
  tenant_id = "",
  realm = "",
  roles = [],
  workspace_client_allowlist = [],
  default_workspace_id = "",
}) {
  const key = getAuthSessionStorageKey();
  const payload = JSON.stringify({
    token,
    email,
    tenant_id: tenant_id != null ? String(tenant_id) : "",
    realm: realm != null ? String(realm) : "",
    roles: Array.isArray(roles) ? roles : [],
    workspace_client_allowlist: Array.isArray(workspace_client_allowlist)
      ? workspace_client_allowlist
      : [],
    default_workspace_id: default_workspace_id != null ? String(default_workspace_id) : "",
  });
  if (rememberMe) {
    window.localStorage.setItem(key, payload);
    window.sessionStorage.removeItem(key);
  } else {
    window.sessionStorage.setItem(key, payload);
    window.localStorage.removeItem(key);
  }
}

export function clearStoredSession() {
  try {
    const key = getAuthSessionStorageKey();
    window.localStorage.removeItem(key);
    window.sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
